import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Sparkles, 
  TrendingUp, 
  Activity, 
  Sliders, 
  FileText, 
  CheckCircle2, 
  AlertCircle, 
  ShieldCheck, 
  ChevronRight, 
  Zap, 
  Download,
  Database,
  LineChart as LineChartIcon
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip as ChartTooltip, 
  ResponsiveContainer, 
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';

interface DrillDownDashboardProps {
  id: string;
  onClose: () => void;
}

// Full specifications for each drill-down profile
const DRILL_DOWN_PROFILES: Record<string, {
  title: string;
  category: string;
  description: string;
  metric: string;
  value: string;
  chartTitle: string;
  chartType: 'bar' | 'line' | 'area';
  chartData: any[];
  chartDataKeys: { key: string; name: string; color: string }[];
  headers: string[];
  tableData: any[];
  aiInsights: string[];
  simulatorTitle: string;
  simulatorDescription: string;
  sliders: {
    key: string;
    label: string;
    min: number;
    max: number;
    step: number;
    defaultVal: number;
    unit: string;
  }[];
  calculateUplift: (sliderVals: Record<string, number>) => {
    primaryLabel: string;
    primaryVal: string;
    secondaryLabel: string;
    secondaryVal: string;
    isPositive: boolean;
  };
  actions: {
    label: string;
    successMessage: string;
    icon: any;
  }[];
}> = {
  'overview-channels': {
    title: 'Unified Channels Deduplication Sync',
    category: 'Operational Finance',
    description: 'Line-by-line financial reconciliation across Shopify DTC storefront and Zoho bulk wholesale invoices.',
    metric: 'De-duplicated Q2 Combined Net',
    value: '₹40,40,000',
    chartTitle: 'Cumulative Net Revenue Pace (Last 15 Days)',
    chartType: 'area',
    chartData: [
      { day: 'Day 1', ShopifyDTC: 180000, ZohoB2B: 110000, Deduplicated: 290000 },
      { day: 'Day 3', ShopifyDTC: 320000, ZohoB2B: 240000, Deduplicated: 560000 },
      { day: 'Day 6', ShopifyDTC: 540000, ZohoB2B: 390000, Deduplicated: 930000 },
      { day: 'Day 9', ShopifyDTC: 790000, ZohoB2B: 480000, Deduplicated: 1270000 },
      { day: 'Day 12', ShopifyDTC: 980000, ZohoB2B: 580000, Deduplicated: 1560000 },
      { day: 'Day 15', ShopifyDTC: 1200000, ZohoB2B: 720000, Deduplicated: 1920000 }
    ],
    chartDataKeys: [
      { key: 'ShopifyDTC', name: 'Shopify DTC', color: '#64748b' },
      { key: 'ZohoB2B', name: 'Zoho B2B Wholesale', color: '#94a3b8' },
      { key: 'Deduplicated', name: 'De-duplicated Net', color: '#ff7a59' }
    ],
    headers: ['Reporting Unit', 'Shopify DTC', 'Zoho B2B Gross', 'Assisted Deductions', 'Reconciled Net'],
    tableData: [
      { unit: 'Month 1 Actuals', DTC: '₹8,50,000', Zoho: '₹4,50,000', Assisted: '- ₹1,20,000', Net: '₹11,80,000' },
      { unit: 'Month 2 Actuals', DTC: '₹9,50,000', Zoho: '₹6,00,000', Assisted: '- ₹1,60,000', Net: '₹13,90,000' },
      { unit: 'Month 3 Actuals', DTC: '₹10,50,000', Zoho: '₹6,20,000', Assisted: '- ₹2,00,000', Net: '₹14,70,000' },
      { unit: 'Combined Q2 Total', DTC: '₹28,50,000', Zoho: '₹16,70,000', Assisted: '- ₹4,80,000', Net: '₹40,40,000' }
    ],
    aiInsights: [
      'The Sales Deduplication Logic resolves a critical operational overlap where B2B corporate bulk orders are recorded in Zoho Books for tax compliance and then manually punched into Shopify to generate fulfillment manifests. Previously, this caused a 12% double-counting error in our cash projection sheets.',
      'By identifying Shopify orders carrying the specific tag "Zoho-B2B-Assisted", the algorithm successfully subtracts ₹4.8L of double-counted value, presenting a true de-duplicated Q2 combined revenue of ₹40.4L.',
      'The next major operational bottleneck is the 6-hour delay in manual data entry of Zoho invoices to Shopify. Deploying a webhook that automatically posts Zoho Payments as zero-value "Prepaid" orders in Shopify is modeled to save 18 hours of manual labor per week.'
    ],
    simulatorTitle: 'Assisted Sync Webhook Simulator',
    simulatorDescription: 'Model the impact of automated Shopify-to-Zoho synchronization. Shorter sync delays prevent manual billing errors and decrease financial audit discrepancies.',
    sliders: [
      { key: 'syncDelay', label: 'Average Sync Delay', min: 1, max: 24, step: 1, defaultVal: 6, unit: 'Hours' },
      { key: 'weeklyOrders', label: 'Manual Order Rate', min: 5, max: 50, step: 5, defaultVal: 15, unit: 'Orders' }
    ],
    calculateUplift: (vals) => {
      const delay = vals.syncDelay || 6;
      const orders = vals.weeklyOrders || 15;
      const hoursSaved = Math.round((delay * 0.15) * orders * 0.5);
      const errorRate = (delay * 0.45).toFixed(1);
      return {
        primaryLabel: 'Weekly Admin Effort Saved',
        primaryVal: `${Math.max(2, hoursSaved)} Hours`,
        secondaryLabel: 'Financial Audit Discrepancy Rate',
        secondaryVal: `${errorRate}%`,
        isPositive: hoursSaved > 5
      };
    },
    actions: [
      { label: 'Trigger API Sync Test', successMessage: 'API Sync Test complete. Reconciled 142 historical Zoho bills with Shopify logs inside 14.5ms. 100% de-duplicated!', icon: Database },
      { label: 'Export Reconciliation Report', successMessage: 'Financial Reconciliation sheet downloaded (CSV format). Ready for accounting sign-off.', icon: Download }
    ]
  },
  'shopify-funnel': {
    title: 'Storefront Checkout & Funnel Friction',
    category: 'Conversion Optimization',
    description: 'Micro-step conversion drop-offs, payment gateway failures, and mobile-responsive checkout speed bottlenecks.',
    metric: 'Shopify Storefront Conversion',
    value: '1.70%',
    chartTitle: 'Hourly Conversion Spikes & Drop-off Ratios (Q2 Average)',
    chartType: 'line',
    chartData: [
      { hour: '08:00', ATC_Rate: 6.8, Checkout_Rate: 3.1, Purchase_Rate: 1.4 },
      { hour: '11:00', ATC_Rate: 8.5, Checkout_Rate: 4.2, Purchase_Rate: 1.8 },
      { hour: '14:00', ATC_Rate: 7.9, Checkout_Rate: 3.9, Purchase_Rate: 1.6 },
      { hour: '17:00', ATC_Rate: 9.1, Checkout_Rate: 4.8, Purchase_Rate: 2.1 },
      { hour: '20:00', ATC_Rate: 11.2, Checkout_Rate: 5.4, Purchase_Rate: 2.4 },
      { hour: '23:00', ATC_Rate: 9.8, Checkout_Rate: 4.5, Purchase_Rate: 1.9 }
    ],
    chartDataKeys: [
      { key: 'ATC_Rate', name: 'Add To Cart %', color: '#94a3b8' },
      { key: 'Checkout_Rate', name: 'Initiate Checkout %', color: '#64748b' },
      { key: 'Purchase_Rate', name: 'Completed Purchase %', color: '#ff7a59' }
    ],
    headers: ['Funnel Stage', 'Sessions Volume', 'Drop-off from Prior Step', 'Benchmark Comparison', 'Calculated Leakage'],
    tableData: [
      { stage: '1. Product Detail Views', value: '124,500', drop: '0.0%', comparison: 'Baseline (100%)', leak: '₹0' },
      { stage: '2. Add to Cart (ATC)', value: '9,960', drop: '92.0% Drop', comparison: '8.0% ATC vs 7.5% Category', leak: '₹12,40,000' },
      { stage: '3. Initiate Checkout', value: '4,482', drop: '55.0% Drop', comparison: '45.0% checkout vs 50.0% standard', leak: '₹24,50,000' },
      { stage: '4. Completed Purchase', value: '2,120', drop: '52.7% Drop', comparison: '47.3% purchase vs 42.0% standard', leak: '₹14,80,000' }
    ],
    aiInsights: [
      'A massive drop-off of 55% occurs from Add to Cart to Initiate Checkout. Heatmap analysis shows that Indian shoppers expect explicit estimated delivery dates (EDDs) on the product page itself. Since EDD is only revealed at the final checkout step, users "mock checkout" to see timelines, inflating cart abandonment.',
      'Secondly, domestic payment gateways (specifically Razorpay CC authorization) suffer from a 12.4% card authentication drop-off on orders above ₹2,000 due to slow bank OTP delivery. Direct UPI intents bypass standard OTP screens, lifting completions.',
      'Recommendation: Adding a zipcode validation widget directly on product detail pages will prevent mock checkouts, keeping clean high-intent users flowing to the billing stage.'
    ],
    simulatorTitle: 'Checkout Friction Optimizer',
    simulatorDescription: 'Model the financial return of reducing checkout fields and enabling Razorpay Flash UPI direct checkout.',
    sliders: [
      { key: 'fieldsRemoved', label: 'Required Fields Removed', min: 0, max: 5, step: 1, defaultVal: 0, unit: 'Fields' },
      { key: 'upiShare', label: 'UPI Direct Payment Share', min: 20, max: 90, step: 5, defaultVal: 35, unit: '%' }
    ],
    calculateUplift: (vals) => {
      const fields = vals.fieldsRemoved || 0;
      const upi = vals.upiShare || 35;
      const fieldConversionGain = fields * 0.08;
      const upiConversionGain = (upi - 35) * 0.012;
      const totalGain = Math.max(0, fieldConversionGain + upiConversionGain);
      const projectedRevenueGained = Math.round(124500 * (0.017 * (1 + totalGain)) * 1344) - (2120 * 1344);
      return {
        primaryLabel: 'Conversion Lift Metric',
        primaryVal: `+${(totalGain * 100).toFixed(2)}%`,
        secondaryLabel: 'Expected Monthly Cash Gain',
        secondaryVal: `+₹${projectedRevenueGained > 0 ? projectedRevenueGained.toLocaleString() : '0'}`,
        isPositive: projectedRevenueGained > 0
      };
    },
    actions: [
      { label: 'Enable Razorpay Flash Checkout', successMessage: 'Razorpay Flash UPI checkout sequence initialized. One-click checkout is now active for mobile browsers. OTP requirement has been bypassed for transactions under ₹5,000.', icon: ShieldCheck },
      { label: 'A/B Test Delivery Timeline Widget', successMessage: 'A/B Test "Pincode EDD Checker" launched. 50% of product page users will now see instant shipping timelines on-screen. Monitoring conversion fluctuations.', icon: Activity }
    ]
  },
  'shopify-retention': {
    title: 'Customer Cohort Repurchase Analytics',
    category: 'Retention Marketing',
    description: 'Deep tracking of repurchase velocities, VIP cohort repetitions, and category cross-purchase sequences.',
    metric: 'Repeat Purchase Rate',
    value: '23.2%',
    chartTitle: 'Cohort Repurchase Curve over Days Post-Purchase',
    chartType: 'area',
    chartData: [
      { days: 'Day 15', DeskMats: 1.2, Sleeves: 0.5, Organizers: 2.1 },
      { days: 'Day 30', DeskMats: 3.8, Sleeves: 1.1, Organizers: 4.5 },
      { days: 'Day 45', DeskMats: 8.5, Sleeves: 2.4, Organizers: 6.8 },
      { days: 'Day 60', DeskMats: 12.4, Sleeves: 3.8, Organizers: 9.2 },
      { days: 'Day 75', DeskMats: 18.2, Sleeves: 4.5, Organizers: 11.4 },
      { days: 'Day 90', DeskMats: 28.5, Sleeves: 5.2, Organizers: 14.2 }
    ],
    chartDataKeys: [
      { key: 'DeskMats', name: 'First Order: Desk Mat', color: '#ff7a59' },
      { key: 'Sleeves', name: 'First Order: Laptop Sleeve', color: '#94a3b8' },
      { key: 'Organizers', name: 'First Order: Tech Organizer', color: '#1e293b' }
    ],
    headers: ['Cohort Group', 'Initial Buyers', 'Day 30 Repurchase', 'Day 60 Repurchase', 'Day 90 Repurchase', 'Top Recurring SKU'],
    tableData: [
      { group: 'Desk Mat XL Cohort', buyers: '984 clients', d30: '3.8%', d60: '12.4%', d90: '28.5%', topSKU: 'Tech Organizer (Charcoal)' },
      { group: 'Laptop Sleeve Cohort', buyers: '682 clients', d30: '1.1%', d60: '3.8%', d90: '5.2%', topSKU: 'Cable Tidy (Tan)' },
      { group: 'Tech Organizer Cohort', buyers: '454 clients', d30: '4.5%', d60: '9.2%', d90: '14.2%', topSKU: 'Desk Mat (Felt)' }
    ],
    aiInsights: [
      'Customers who purchase a "Desk Mat" as their initial item are extremely valuable. They exhibit a 28.5% repeat purchase rate within 90 days, primarily buying a color-coordinated Tech Organizer to complete their workspace aesthetic.',
      'Conversely, Laptop Sleeve buyers repeat at just 5.2% inside 90 days, treating sleeves as a single-use protective commodity. This indicates we should immediately pivot post-purchase flows based on initial SKU categories rather than generic drip campaigns.',
      'By triggering a tailored accent-color cross-sell offer exactly on Day 45 (offering a matching organizer or tidy with a VIP appreciation discount), we can convert Desk Mat buyers to repeat customers with zero ad spend.'
    ],
    simulatorTitle: 'Cross-Sell Trigger Optimizer',
    simulatorDescription: 'Model the impact of post-purchase discount coupons and automated SMS send intervals on Day-45 custom email flow.',
    sliders: [
      { key: 'couponSize', label: 'VIP Coupon Discount', min: 5, max: 25, step: 5, defaultVal: 10, unit: '%' },
      { key: 'smsAlerts', label: 'Post-Purchase SMS Frequency', min: 1, max: 3, step: 1, defaultVal: 1, unit: 'Texts' }
    ],
    calculateUplift: (vals) => {
      const discount = vals.couponSize || 10;
      const texts = vals.smsAlerts || 1;
      const expectedLift = (discount * 0.18) + (texts * 0.12);
      const finalRepeatRate = (23.2 + expectedLift).toFixed(1);
      const monthlyAdSpendSaved = Math.round(expectedLift * 24000);
      return {
        primaryLabel: 'Modeled Repeat Rate',
        primaryVal: `${finalRepeatRate}%`,
        secondaryLabel: 'CAC Re-investment Savings',
        secondaryVal: `₹${monthlyAdSpendSaved.toLocaleString()}`,
        isPositive: expectedLift > 1
      };
    },
    actions: [
      { label: 'Sync VIP Segment to Klaviyo', successMessage: 'Sync complete. 492 repeat buyers with LTV > ₹4,000 synced as "VIP High Value Cohort" list inside Klaviyo. Custom webhook automated.', icon: Database },
      { label: 'Draft Day-45 Email Flow', successMessage: 'Post-purchase Day-45 automated cross-sell sequence draft created in Shopify Email. Target: Offering Tech Pouches to Tan leather Desk Mat buyers.', icon: FileText }
    ]
  },
  'meta-campaigns': {
    title: 'Meta Ad Set Granularity & Attributions',
    category: 'Performance Marketing',
    description: 'Detailed analysis of Facebook/Instagram campaign spend distributions, custom creative hook ratios, and creative fatigue.',
    metric: 'Meta Blended ROAS',
    value: '3.12x',
    chartTitle: 'Cumulative Daily ROAS Velocity vs Budget Scale',
    chartType: 'line',
    chartData: [
      { spend: '₹10k/day', Ad1_Reels_ROAS: 5.12, Ad2_Carousel_ROAS: 3.82, Ad4_Broad_ROAS: 1.42 },
      { spend: '₹20k/day', Ad1_Reels_ROAS: 4.85, Ad2_Carousel_ROAS: 3.60, Ad4_Broad_ROAS: 1.10 },
      { spend: '₹30k/day', Ad1_Reels_ROAS: 4.34, Ad2_Carousel_ROAS: 3.22, Ad4_Broad_ROAS: 0.85 },
      { spend: '₹40k/day', Ad1_Reels_ROAS: 3.90, Ad2_Carousel_ROAS: 2.85, Ad4_Broad_ROAS: 0.65 }
    ],
    chartDataKeys: [
      { key: 'Ad1_Reels_ROAS', name: 'Ad #1 (Desk Setup Video)', color: '#ff7a59' },
      { key: 'Ad2_Carousel_ROAS', name: 'Ad #2 (Leather Carousel)', color: '#1e293b' },
      { key: 'Ad4_Broad_ROAS', name: 'Ad #4 (Broad Gift Carousel)', color: '#94a3b8' }
    ],
    headers: ['Creative Identifier', 'Format / Type', 'Spend Weight', 'CPP (Cost/Sale)', 'Hook Rate (3s)', 'Attributed ROAS'],
    tableData: [
      { id: 'Ad #1 (Organized Setup)', type: '9:16 Vertical Reel', spend: '₹2,50,000 (41%)', cpp: '₹420', hook: '42.8%', roas: '4.85x' },
      { id: 'Ad #2 (Folio Sleeve)', type: '1:1 Product Carousel', spend: '₹1,80,000 (29%)', cpp: '₹510', hook: '21.5%', roas: '3.60x' },
      { id: 'Ad #3 (Color Range)', type: '1:1 Static Image', spend: '₹1,10,000 (18%)', cpp: '₹620', hook: '18.4%', roas: '2.85x' },
      { id: 'Ad #4 (Broad Holiday)', type: '1:1 Lifestyle Carousel', spend: '₹70,000 (12%)', cpp: '₹1,450', hook: '11.2%', roas: '1.10x' }
    ],
    aiInsights: [
      'Vertical video reels are significantly outperforming static carousel ads. Premium desk accessories are lifestyle statements that require motion and spatial context to convey value. Studio-lit carousels fail to captivate attention in broad feeds, leading to high cost-per-purchases (₹1,450 on Ad #4).',
      'The "stitching detail close-up" hook used in the first 2.5 seconds of Ad #1 maintains a high 42.8% retention rate. Replicating this hook across our secondary product lines is our best scaling vector.',
      'We must immediately pause Ad #4 (Broad Holiday Gifting) and reallocate the ₹70,000 budget to Google Search Ads and Ad #1 variations. This prevents budget dilution and lifts blended ROAS.'
    ],
    simulatorTitle: 'Meta Budget Reallocation Modeler',
    simulatorDescription: 'Model the impact of shifting Meta budget from general corporate carousel placements to vertical reels.',
    sliders: [
      { key: 'reallocPercent', label: 'Budget Shift to Reels', min: 0, max: 100, step: 10, defaultVal: 0, unit: '%' }
    ],
    calculateUplift: (vals) => {
      const shift = vals.reallocPercent || 0;
      const expectedBlendedROAS = (3.12 + (shift * 0.0035)).toFixed(2);
      const budgetSavedFromWastage = Math.round(70000 * (shift / 100));
      return {
        primaryLabel: 'Blended Meta ROAS Model',
        primaryVal: `${expectedBlendedROAS}x`,
        secondaryLabel: 'Wasted Spend Recovered',
        secondaryVal: `₹${budgetSavedFromWastage.toLocaleString()}`,
        isPositive: shift > 0
      };
    },
    actions: [
      { label: 'Pause Holiday Carousel Ad Set', successMessage: 'Ad Set "Holiday Gifting (General Interests)" deactivated in Meta Ads Manager. ₹70,000 spend cap has been paused to halt wastage.', icon: ShieldCheck },
      { label: 'Dispatch Brief to Agency', successMessage: 'Agency task brief generated and sent. Required: 3 new 9:16 vertical reel hooks showcasing leather stitching close-ups for the Laptop Sleeves category.', icon: FileText }
    ]
  },
  'google-campaigns': {
    title: 'Google SEM Keyword Share & Bid Limits',
    category: 'Paid Search Operations',
    description: 'Exact-match trademark protection, competitor bidding alerts, search impression shares, and generic non-brand bidding margins.',
    metric: 'Google Blended ROAS',
    value: '4.09x',
    chartTitle: 'Search Impression Share Loss vs CPC Bid Density',
    chartType: 'bar',
    chartData: [
      { bidLevel: 'Baseline (1x)', ImpressionShare: 78, CompetitorClickLeak: 22, Conversions: 210 },
      { bidLevel: 'Optimized (1.3x)', ImpressionShare: 89, CompetitorClickLeak: 11, Conversions: 268 },
      { bidLevel: 'Aggressive (1.6x)', ImpressionShare: 96, CompetitorClickLeak: 4, Conversions: 312 },
      { bidLevel: 'Capped (2x)', ImpressionShare: 98, CompetitorClickLeak: 2, Conversions: 320 }
    ],
    chartDataKeys: [
      { key: 'ImpressionShare', name: 'Nooe Impression Share %', color: '#1e293b' },
      { key: 'CompetitorClickLeak', name: 'Competitor Click Leak %', color: '#ff7a59' }
    ],
    headers: ['Search Query Phrase', 'Intent Split', 'Impression Share', 'Average CPC', 'Monthly Conversions', 'Query-Level ROAS'],
    tableData: [
      { query: 'nooe desk mat premium', intent: 'Brand Protection', share: '94.2%', cpc: '₹12', conv: '312', roas: '8.40x' },
      { query: 'premium leather desk mat', intent: 'Generic High Intent', share: '68.5%', cpc: '₹34', conv: '184', roas: '4.10x' },
      { query: 'minimalist desk organizer', intent: 'Generic High Intent', share: '62.0%', cpc: '₹28', conv: '96', roas: '3.50x' },
      { query: 'leather tech pouch organizer', intent: 'Generic High Intent', share: '59.1%', cpc: '₹32', conv: '88', roas: '3.20x' },
      { query: 'minimalist office setups India', intent: 'General Discovery', share: '45.0%', cpc: '₹18', conv: '42', roas: '1.80x' }
    ],
    aiInsights: [
      'Our Google Brand SEM is working incredibly well (8.40 ROAS), but competitors are actively bidding on our brand phrase "nooe desk mat" to siphon off organic traffic. Bumping bid caps guarantees a 95%+ Impression Share and blocks competitive leakage.',
      'Generic search terms like "premium leather desk mat" are highly profitable but are constrained by strict daily budgets. Shifting ₹25,000 from low-intent "office setup inspiration" blogs to generic keyword categories will capture immediate buyer intent.',
      'Deploying custom lightweight landing pages that contain single-click UPI payment links will raise generic search landing conversion rates from 1.7% to 2.2% without inflating bid CPCs.'
    ],
    simulatorTitle: 'Generic Search Bid Maximizer',
    simulatorDescription: 'Model the impact of increasing bid coefficients specifically on high-performing generic intent terms.',
    sliders: [
      { key: 'bidMultiplier', label: 'CPC Bid Multiplier', min: 1.0, max: 2.0, step: 0.1, defaultVal: 1.0, unit: 'x' }
    ],
    calculateUplift: (vals) => {
      const multiplier = vals.bidMultiplier || 1.0;
      const expectedImpressionShare = Math.min(98, Math.round(65 * multiplier));
      const expectedRevenueAdded = Math.round((multiplier - 1) * 115000);
      return {
        primaryLabel: 'Projected Impression Share',
        primaryVal: `${expectedImpressionShare}%`,
        secondaryLabel: 'Incremental Search Revenue',
        secondaryVal: `₹${expectedRevenueAdded.toLocaleString()}`,
        isPositive: expectedRevenueAdded > 0
      };
    },
    actions: [
      { label: 'Enforce Brand Search Bid Floor', successMessage: 'Automated bidding rule deployed. Google Ads will maintain absolute top of page bid floor for "nooe" trademark phrases, capping competitor leaks.', icon: ShieldCheck },
      { label: 'Prune Low-Intent Placements', successMessage: 'Google Ads negative match list updated. Excluded 42 unoptimized office blog and wallpaper discovery forums from PMax target networks.', icon: FileText }
    ]
  },
  'bitespeed-cx': {
    title: 'WhatsApp Conversational Revenue & CSAT',
    category: 'Conversational CRM',
    description: 'WhatsApp automated cart recovery sequence timings, user response response latencies, and support feedback analysis.',
    metric: 'WhatsApp Recovered Sales',
    value: '₹3,42,000',
    chartTitle: 'Cart Recovery Conversion Rate vs Delay Time (Hours)',
    chartType: 'line',
    chartData: [
      { delay: '15 Mins', RecoveryRate: 4.2, UnsubscribeRate: 0.3 },
      { delay: '30 Mins', RecoveryRate: 5.8, UnsubscribeRate: 0.5 },
      { delay: '1 Hour', RecoveryRate: 6.6, UnsubscribeRate: 0.8 },
      { delay: '3 Hours', RecoveryRate: 5.1, UnsubscribeRate: 1.2 },
      { delay: '6 Hours', RecoveryRate: 3.4, UnsubscribeRate: 1.8 },
      { delay: '12 Hours', RecoveryRate: 2.2, UnsubscribeRate: 2.4 }
    ],
    chartDataKeys: [
      { key: 'RecoveryRate', name: 'Recovery Rate %', color: '#ff7a59' },
      { key: 'UnsubscribeRate', name: 'Unsubscribe Rate %', color: '#94a3b8' }
    ],
    headers: ['WhatsApp Automation Flow', 'Sent Count', 'Read Count', 'Orders Recovered', 'CSAT Rating Contribution', 'Recovered Revenue'],
    tableData: [
      { flow: 'Hour 1 Ping (Checkout abandoned)', sent: '480 pings', read: '410 (85.4%)', recovered: '32 orders', rating: '4.85 / 5', rev: '₹43,000' },
      { flow: 'Hour 12 Ping (5% discount offer)', sent: '390 pings', read: '320 (82.1%)', recovered: '18 orders', rating: '4.68 / 5', rev: '₹2,42,000' },
      { flow: 'Hour 24 Ping (Final deadline limit)', sent: '340 pings', read: '290 (85.2%)', recovered: '10 orders', rating: '4.72 / 5', rev: '₹57,000' }
    ],
    aiInsights: [
      'The Hour 1 WhatsApp Ping captures 68% of total recovered carts. Checkout intent is extremely fresh, and sending a helpful check-in message containing a direct checkout link resolves 6.6% of abandoned carts immediately.',
      'The Hour 12 Ping offers a 5% discount, recovering ₹2.42L, but carries the highest customer opt-out click rate (2.4%). To prevent brand dilution, we should replace the standard text block with an interactive rich-media template showing a short video of the desk mat unboxing experience.',
      'Our first response time stands outstandingly at 4.5 minutes. Pragatee manages 92% of tickets within standard SLA levels. CSAT remains high at 4.76/5, driven by swift resolution of shipping delays.'
    ],
    simulatorTitle: 'Recovery Ping Delay Optimizer',
    simulatorDescription: 'Adjust the automated delay for the first checkout recovery message to find the conversion sweet spot before unsubscribe friction sets in.',
    sliders: [
      { key: 'firstDelay', label: 'First Ping Delay (Mins)', min: 15, max: 180, step: 15, defaultVal: 60, unit: 'Mins' }
    ],
    calculateUplift: (vals) => {
      const delay = vals.firstDelay || 60;
      let recoveryVal = 6.6;
      let CSATForecast = '4.76';
      if (delay < 30) {
        recoveryVal = 4.8;
        CSATForecast = '4.62 (Too intrusive)';
      } else if (delay === 30) {
        recoveryVal = 5.8;
        CSATForecast = '4.72';
      } else if (delay === 60) {
        recoveryVal = 6.6;
        CSATForecast = '4.76 (Optimal)';
      } else if (delay > 60) {
        recoveryVal = Math.max(2.0, 6.6 - ((delay - 60) * 0.03));
        CSATForecast = '4.78';
      }
      const projectedTotalRecovered = Math.round(480 * (recoveryVal / 100) * 1344);
      return {
        primaryLabel: 'Model Recovery Rate',
        primaryVal: `${recoveryVal.toFixed(1)}%`,
        secondaryLabel: 'Model CSAT Forecast',
        secondaryVal: CSATForecast,
        isPositive: recoveryVal >= 5.5
      };
    },
    actions: [
      { label: 'Activate UPI Recovery Templates', successMessage: 'Active template changed inside Bitespeed API. Cart recovery messages will now contain a direct Razorpay UPI instant-paying button instead of storefront URLs.', icon: ShieldCheck },
      { label: 'Export Surveys to Sheet', successMessage: 'Successfully transmitted 740 post-purchase survey logs into Pragatee\'s master Google Sheet. Alignment is 100% active.', icon: Database }
    ]
  },
  'unicommerce-logistics': {
    title: 'Fulfillment Logistics & Inventory Health',
    category: 'Logistics Operations',
    description: 'Detailed analysis of warehouse stages, manifest counts, carrier pickup SLA latencies, and product category stockout trends.',
    metric: 'Dispatch Latency SLA',
    value: '1.2 Days',
    chartTitle: 'Out-Of-Stock Rate Trends (Last 6 Months)',
    chartType: 'line',
    chartData: [
      { month: 'Jan', CatalogOOS: 10.2, ATierOOS: 14.5 },
      { month: 'Feb', CatalogOOS: 11.4, ATierOOS: 16.2 },
      { month: 'Mar', CatalogOOS: 12.8, ATierOOS: 19.8 },
      { month: 'Apr', CatalogOOS: 14.2, ATierOOS: 22.4 },
      { month: 'May', CatalogOOS: 15.8, ATierOOS: 25.1 },
      { month: 'Jun', CatalogOOS: 16.9, ATierOOS: 28.5 }
    ],
    chartDataKeys: [
      { key: 'CatalogOOS', name: 'Catalog-wide OOS %', color: '#64748b' },
      { key: 'ATierOOS', name: 'A-Tier (Peak SKUs) OOS %', color: '#ff7a59' }
    ],
    headers: ['Warehouse Stage', 'Orders in Queue', 'Average Queue Latency', 'Responsible Carrier', 'Alert Status'],
    tableData: [
      { stage: 'Pending Manifest Validation', count: '18 orders', latency: '4.2 Hours', carrier: 'Manual Sync - Unicommerce', alert: 'Normal' },
      { stage: 'Manifest Created (Packing)', count: '42 orders', latency: '12.0 Hours', carrier: 'Delhi Central Facility', alert: 'Normal' },
      { stage: 'Awaiting Carrier Pickup', count: '24 orders', latency: '8.4 Hours', carrier: 'BlueDart Ground Cargo', alert: 'Delay (Rain)' },
      { stage: 'Carrier Dispatched', count: '1,420 orders', latency: '1.2 Days', carrier: 'Shiprocket Delhi (Air)', alert: 'Excellent' }
    ],
    aiInsights: [
      'The Delhi fulfillment warehouse is operating at a solid 1.2-day average dispatch latency. However, high volume days cause packaging-material bottlenecks, particularly on large leather desk mats which require customized cardboard flat-packs.',
      'The catalog-wide stock-out of 16.9% represents a massive ₹3.8L in missed sales opportunity. Our A-tier products (Charcoal and Tan desk mats) are holding a critical 28.5% stockout rate due to a 3-week delays in raw-material supply from the tannery.',
      'Recommendation: Raising safety stock thresholds inside Unicommerce will auto-trigger replenishment orders 15 days earlier, shielding against future supplier manufacturing lag.'
    ],
    simulatorTitle: 'Reorder Buffer Optimizer',
    simulatorDescription: 'Adjust restocking lead times and safety buffer thresholds to see the modeled drop in stock-out probability.',
    sliders: [
      { key: 'leadBuffer', label: 'Safety Lead Time', min: 0, max: 30, step: 5, defaultVal: 10, unit: 'Days' }
    ],
    calculateUplift: (vals) => {
      const buffer = vals.leadBuffer || 10;
      const expectedOOS = Math.max(5.0, 28.5 - (buffer * 0.75)).toFixed(1);
      const preventedLoss = Math.round(buffer * 12500);
      return {
        primaryLabel: 'A-Tier Stockout Probability',
        primaryVal: `${expectedOOS}%`,
        secondaryLabel: 'Prevented Revenue Loss',
        secondaryVal: `₹${preventedLoss.toLocaleString()}`,
        isPositive: buffer > 10
      };
    },
    actions: [
      { label: 'Raise Restocking Thresholds', successMessage: 'Safety thresholds updated in Unicommerce inventory rules. Top 12 high-velocity SKUs will now auto-alert at 80 units.', icon: ShieldCheck },
      { label: 'Trigger Replenishment PO', successMessage: 'Purchase Order draft raised in Zoho Books. 500 units of Charcoal Leather Desk Mat XL requested from Chennai facility.', icon: FileText }
    ]
  }
};

export default function DrillDownDashboard({ id, onClose }: DrillDownDashboardProps) {
  const profile = DRILL_DOWN_PROFILES[id] || DRILL_DOWN_PROFILES['overview-channels'];
  
  // Dynamic Slider States
  const [sliderVals, setSliderVals] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    profile.sliders.forEach(s => {
      initial[s.key] = s.defaultVal;
    });
    return initial;
  });

  // Action Success Toast State
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleSliderChange = (key: string, val: number) => {
    setSliderVals(prev => ({ ...prev, [key]: val }));
  };

  const handleTriggerAction = (actionLabel: string, successMsg: string) => {
    setToastMessage(successMsg);
    setTimeout(() => {
      setToastMessage(null);
    }, 4500);
  };

  const simulatedMetrics = profile.calculateUplift(sliderVals);

  return (
    <div className="space-y-6 animate-fade-in font-sans relative pb-10">
      
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-6 right-6 z-50 max-w-md bg-zinc-900 border border-emerald-500/30 p-4 rounded-xl shadow-2xl flex gap-3 animate-fade-in">
          <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="text-left">
            <span className="text-[10px] font-mono text-emerald-400 font-bold block uppercase tracking-wider">ACTION TRIGGERED</span>
            <p className="text-xs text-zinc-300 mt-1 font-medium leading-relaxed">
              {toastMessage}
            </p>
          </div>
        </div>
      )}

      {/* Back Header navigation */}
      <div className="flex items-center justify-between border-b border-slate-200/60 pb-5">
        <div className="flex items-center gap-4">
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-200/60 rounded-xl transition-all border border-slate-200/40 text-slate-600 bg-white shadow-xs group"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          </button>
          <div className="text-left">
            <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400 font-bold block">
              {profile.category} • Granular Operations Dashboard
            </span>
            <h1 className="text-2xl font-semibold font-arial text-slate-800 tracking-tight flex items-center gap-2 mt-0.5">
              {profile.title}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/15 px-3 py-1.5 rounded-full text-xs font-semibold text-orange-600">
          <Activity className="h-3.5 w-3.5 animate-pulse" />
          <span>Operational Drill-Down Mode</span>
        </div>
      </div>

      {/* Primary KPI Header row */}
      <div className="p-6 bg-white border border-slate-200/80 rounded-3xl shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1 text-left md:max-w-2xl">
          <h2 className="text-sm font-semibold text-slate-400 uppercase font-mono tracking-wider">Functional Area Scope</h2>
          <p className="text-sm text-slate-600 leading-relaxed font-medium">
            {profile.description}
          </p>
        </div>
        <div className="p-4 bg-slate-50 border border-slate-200/60 rounded-2xl min-w-[200px] text-left">
          <span className="text-[9px] font-mono text-slate-400 uppercase tracking-widest font-bold block">{profile.metric}</span>
          <span className="text-2xl font-mono font-black text-slate-800 tracking-tight block mt-0.5">{profile.value}</span>
        </div>
      </div>

      {/* Granular Chart vs What-if Simulator Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* High Density Chart Panel */}
        <div className="lg:col-span-2 bg-white border border-slate-200/80 p-6 rounded-3xl shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="font-display font-bold text-slate-800 text-xs tracking-wide uppercase flex items-center gap-2">
              <LineChartIcon className="h-4 w-4 text-orange-500" />
              {profile.chartTitle}
            </h3>
            <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest">Granular Trend</span>
          </div>

          <div className="h-72 w-full pt-2">
            <ResponsiveContainer width="100%" height="100%">
              {profile.chartType === 'area' ? (
                <AreaChart data={profile.chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorDeduplicated" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ff7a59" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#ff7a59" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(0,0,0,0.02)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey={profile.chartData[0].day ? 'day' : 'days'} stroke="#64748b" fontSize={10} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                  <ChartTooltip 
                    contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} 
                    labelStyle={{ fontSize: '10px', color: '#1e293b', fontWeight: 'bold' }}
                  />
                  {profile.chartDataKeys.map((dk, idx) => (
                    <Area 
                      key={idx}
                      type="monotone" 
                      dataKey={dk.key} 
                      name={dk.name} 
                      stroke={dk.color} 
                      fill={dk.key === 'Deduplicated' ? 'url(#colorDeduplicated)' : 'none'}
                      strokeWidth={dk.key === 'Deduplicated' ? 2.5 : 1.5}
                      dot={dk.key === 'Deduplicated' ? { r: 4 } : false}
                    />
                  ))}
                </AreaChart>
              ) : profile.chartType === 'line' ? (
                <LineChart data={profile.chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(0,0,0,0.02)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey={profile.chartData[0].hour ? 'hour' : 'delay'} stroke="#64748b" fontSize={10} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                  <ChartTooltip 
                    contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} 
                    labelStyle={{ fontSize: '10px', color: '#1e293b', fontWeight: 'bold' }}
                  />
                  {profile.chartDataKeys.map((dk, idx) => (
                    <Line 
                      key={idx}
                      type="monotone" 
                      dataKey={dk.key} 
                      name={dk.name} 
                      stroke={dk.color} 
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              ) : (
                <BarChart data={profile.chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(0,0,0,0.02)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="bidLevel" stroke="#64748b" fontSize={10} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                  <ChartTooltip 
                    contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} 
                    labelStyle={{ fontSize: '10px', color: '#1e293b', fontWeight: 'bold' }}
                  />
                  {profile.chartDataKeys.map((dk, idx) => (
                    <Bar 
                      key={idx}
                      dataKey={dk.key} 
                      name={dk.name} 
                      fill={dk.color} 
                      radius={[4, 4, 0, 0]}
                    />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* AI Operational Simulator */}
        <div className="bg-white border border-slate-200/80 p-6 rounded-3xl shadow-sm space-y-5 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-display font-bold text-slate-800 text-xs tracking-wide uppercase flex items-center gap-1.5">
                <Sliders className="h-4 w-4 text-orange-500" />
                {profile.simulatorTitle}
              </h3>
              <span className="px-2 py-0.5 rounded-full text-[8px] font-mono bg-orange-50 text-orange-700 border border-orange-100 font-bold uppercase tracking-wider">
                AI COGNITIVE MODEL
              </span>
            </div>

            <p className="text-xs text-slate-500 leading-normal font-sans text-left font-medium">
              {profile.simulatorDescription}
            </p>

            {/* Sliders Container */}
            <div className="space-y-4 pt-2">
              {profile.sliders.map(slider => (
                <div key={slider.key} className="space-y-1.5 text-left">
                  <div className="flex justify-between text-xs font-semibold">
                    <span className="text-slate-500">{slider.label}</span>
                    <span className="font-mono text-orange-600">
                      {sliderVals[slider.key] || slider.defaultVal} {slider.unit}
                    </span>
                  </div>
                  <input 
                    type="range" 
                    min={slider.min} 
                    max={slider.max} 
                    step={slider.step} 
                    value={sliderVals[slider.key] || slider.defaultVal} 
                    onChange={(e) => handleSliderChange(slider.key, Number(e.target.value))}
                    className="w-full h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-orange-500"
                  />
                  <div className="flex justify-between text-[9px] text-slate-400 font-bold font-mono">
                    <span>{slider.min} {slider.unit}</span>
                    <span>{slider.max} {slider.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Simulated Forecast output */}
          <div className="p-4 bg-orange-500/[0.02] border border-orange-500/10 rounded-2xl space-y-3 font-sans text-left mt-4">
            <div>
              <span className="text-[10px] font-bold font-mono uppercase tracking-widest text-slate-400 block">
                {simulatedMetrics.primaryLabel}
              </span>
              <span className="text-xl font-mono font-black text-slate-800 tracking-tight">
                {simulatedMetrics.primaryVal}
              </span>
            </div>
            <div>
              <span className="text-[10px] font-bold font-mono uppercase tracking-widest text-slate-400 block">
                {simulatedMetrics.secondaryLabel}
              </span>
              <span className="text-sm font-mono font-bold text-emerald-600">
                {simulatedMetrics.secondaryVal}
              </span>
            </div>
          </div>

        </div>
      </div>

      {/* Line-level Data Table */}
      <div className="bg-white border border-slate-200/80 p-6 rounded-3xl shadow-sm space-y-4 text-left">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <h3 className="font-display font-bold text-slate-800 text-xs tracking-wide uppercase flex items-center gap-1.5">
            <FileText className="h-4 w-4 text-orange-500" />
            Granular Transactional Records (Q2 Logs)
          </h3>
          <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest">Audit Trails</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-sans">
            <thead>
              <tr className="border-b border-slate-200 text-slate-400 font-mono uppercase tracking-wider text-[9px] font-bold">
                {profile.headers.map((h, i) => (
                  <th key={i} className="pb-3 pr-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {profile.tableData.map((row, idx) => (
                <tr key={idx} className="group hover:bg-slate-50/50">
                  {Object.values(row).map((cell: any, cidx) => (
                    <td key={cidx} className="py-3.5 pr-4 text-slate-700 font-medium font-sans">
                      {typeof cell === 'string' && cell.startsWith('₹') || cell.startsWith('- ₹') || cell.includes('%') ? (
                        <span className="font-mono text-slate-800 font-semibold">{cell}</span>
                      ) : cell === 'Normal' || cell === 'Excellent' ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">{cell}</span>
                      ) : cell.includes('Delay') ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-50 text-orange-700 border border-orange-100">{cell}</span>
                      ) : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deepest Operational AI Insights panel & Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* Deepest AI Insights Panel */}
        <div className="md:col-span-2 bg-zinc-900 text-zinc-100 p-6 rounded-3xl shadow-lg space-y-4 text-left">
          <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-orange-400 animate-pulse" />
              <h3 className="font-mono font-bold text-zinc-400 text-xs uppercase tracking-widest">
                Deepest AI Operations Analysis
              </h3>
            </div>
            <span className="px-2 py-0.5 rounded-full text-[8.5px] font-mono bg-orange-400/10 text-orange-400 border border-orange-400/15 font-bold uppercase tracking-wider">
              Nooe Strategy Node
            </span>
          </div>

          <div className="space-y-4 pt-1">
            {profile.aiInsights.map((insight, idx) => (
              <div key={idx} className="flex gap-3 items-start">
                <ChevronRight className="h-4 w-4 text-orange-400 shrink-0 mt-1" />
                <p className="text-xs text-zinc-300 leading-relaxed font-sans font-medium">
                  {insight}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Action Panel */}
        <div className="bg-white border border-slate-200/80 p-6 rounded-3xl shadow-sm flex flex-col justify-between">
          <div className="space-y-3 text-left">
            <h3 className="font-display font-bold text-slate-800 text-xs tracking-wide uppercase border-b border-slate-100 pb-3">
              Deploy Decisions Directly
            </h3>
            <p className="text-xs text-slate-500 leading-normal font-sans font-medium">
              Execute immediate overrides and automated synchronizations directly from this panel to Shopify and Zoho.
            </p>
          </div>

          <div className="space-y-3 mt-6">
            {profile.actions.map((act, idx) => {
              const ActionIcon = act.icon;
              return (
                <button
                  key={idx}
                  onClick={() => handleTriggerAction(act.label, act.successMessage)}
                  className="w-full flex items-center justify-between p-3.5 bg-slate-50 hover:bg-slate-100 rounded-2xl border border-slate-200/60 text-slate-700 text-xs font-semibold tracking-wide transition-all active:scale-98 shadow-xs hover:border-slate-300 text-left cursor-pointer group"
                >
                  <span className="flex items-center gap-2.5">
                    <ActionIcon className="h-4.5 w-4.5 text-orange-500 group-hover:scale-110 transition-transform" />
                    {act.label}
                  </span>
                  <Zap className="h-3.5 w-3.5 text-slate-400 opacity-60 group-hover:translate-x-0.5 transition-transform" />
                </button>
              );
            })}
          </div>
        </div>

      </div>

    </div>
  );
}
